import { SecondaryButton } from "@brexhq/component-library";
import { useTheme } from "@emotion/react";
import styled from "@emotion/styled";
import { margin, padding } from "polished";
import React, { useMemo, useState, useCallback } from "react";

import { SignupNavigationConfig } from "@brexhq/next/sanity-typegen";
import { Container } from "@brexhq/next/src/components/layout/Container/Container";
import { ResponsiveIcon } from "@brexhq/next/src/components/shared/Icon/";
import { Link } from "@brexhq/next/src/components/shared/Link";
import { useSSUPrefillContext } from "@brexhq/next/src/pageTemplates/SignupPage/utils/SSUPrefillContext";
import { Text } from "@brexhq/shared/components/Typography/Text";

import { UserPrefillData } from "../../../routes/signup/Form";

import { BrexFlagBlackBG } from "./LeadForm.elements";
import { useHideNavbarOnScroll } from "./hooks/useHideNavbarOnScroll";

const NavBarLogoBorder = styled.div<{ $shouldStack?: boolean }>(
  ({ theme, $shouldStack }) => ({
    borderRight: `1px solid ${theme.colors.border.default}`,
    ...padding(0, theme.spacing.m16, 0, 0),
    ...margin(0, theme.spacing.m16, 0, 0),

    // Apply stacking styles only on mobile AND when shouldStack is true
    [theme.breakpoints.phone]: {
      ...($shouldStack && {
        borderRight: "none",
        borderBottom: `1px solid ${theme.colors.border.default}`,
        ...padding(
          theme.spacing.m4,
          theme.spacing.m16,
          theme.spacing.m4,
          theme.spacing.m16,
        ),
        ...margin(theme.spacing.m16, 0, 0, 0),
      }),
    },
  }),
);

const PartnerLogo = styled.img({
  objectFit: "contain",
  maxHeight: 27,
  objectPosition: "left",
  width: "auto",
});

const LeadFormNavContainer = styled.div<{ $isVisible: boolean }>(
  ({ theme, $isVisible }) => ({
    zIndex: theme.zIndex.HEADER,
    display: "flex",
    position: "absolute",
    alignItems: "center",
    justifyContent: "flex-end",
    width: "100%",
    height: 80,
    transform: $isVisible ? "translateY(0)" : "translateY(-100%)",
    transition: "transform 0.2s cubic-bezier(0.4, 0, 0.2, 1)",
    willChange: "transform",
    ...padding(
      theme.spacing.SML.xxsml,
      theme.spacing.SML.sml,
      theme.spacing.SML.xxsml,
      theme.spacing.SML.sml,
    ),
    [theme.breakpoints.MED]: {
      height: 60,
      backgroundColor: "transparent",
      display: "flex",
      ...padding(
        theme.spacing.SML.xxsml,
        theme.spacing.SML.sml,
        theme.spacing.SML.xxsml,
        theme.spacing.SML.sml,
      ),
    },
    // Only apply transform on medium screens and below
    [theme.breakpoints.LRG]: {
      transform: "translateY(0) !important",
      transition: "none",
    },
  }),
);

const BrexLogoButtonContainerDesktop = styled(Container)(({ theme }) => ({
  flexDirection: "row",
  display: "flex",
  alignItems: "center",
  marginRight: "auto",
  paddingRight: theme.spacing.SML.sml,
  [theme.breakpoints.MED]: {
    display: "none",
  },
}));

const BrexLogoButtonContainerTabletAndMobile = styled(Container)(
  ({ theme }) => ({
    flexDirection: "row",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    marginRight: "auto",
    [theme.breakpoints.LRG]: {
      display: "none",
    },
  }),
);

const SignInLink = styled(Link)(({ theme }) => ({
  textDecoration: "underline",
  color: theme.colors.content.primary,
}));

const MobileLogoAndPartnerLogoContainer = styled.div<{ $shouldStack: boolean }>(
  ({ theme, $shouldStack }) => ({
    display: "flex",
    alignItems: "center",
    // justifyContent: "center",

    // Only stack on mobile when shouldStack is true
    [theme.breakpoints.phone]: {
      flexDirection: $shouldStack ? "column" : "row",
    },

    // Default to row on larger screens
    flexDirection: "row",
  }),
);

const BrexLogoNavButton: React.FC<{ partnerLogo?: string }> = ({
  partnerLogo,
}) => {
  const [shouldStackLogos, setShouldStackLogos] = useState(false);

  const handlePartnerLogoLoad = useCallback(
    (event: React.SyntheticEvent<HTMLImageElement>) => {
      const img = event.currentTarget;
      const aspectRatio = img.naturalWidth / img.naturalHeight;
      // Stack if aspect ratio is significantly different from 1:1 (more than 0.3 away from 1)
      const isCloseToSquare = Math.abs(aspectRatio - 1) <= 0.3;
      setShouldStackLogos(!isCloseToSquare);
    },
    [],
  );
  const BrexLogoDesktop = () => (
    <Link
      modifier="unstyled"
      component={"nav"}
      section="logo"
      to="/"
      label="Brex logo"
      aria-label="Brex logo"
      margin={[0, 0, 0, 0]}
    >
      <BrexFlagBlackBG height={48} width={48} />
    </Link>
  );
  const LogoDesktop = () => (
    <>
      {partnerLogo ? (
        <>
          <NavBarLogoBorder>
            <BrexLogoDesktop />
          </NavBarLogoBorder>
          <PartnerLogo src={partnerLogo} onLoad={handlePartnerLogoLoad} />
        </>
      ) : (
        <BrexLogoDesktop />
      )}
    </>
  );

  const BrexLogoTabletMobile = () => (
    <Link
      modifier="unstyled"
      component={"nav"}
      section="logo"
      to="/"
      label="Brex logo"
      aria-label="Brex logo"
      margin={[0, 0, 0, 0]}
    >
      {partnerLogo ? (
        // Use desktop logo (BrexFlagBlackBG) when partner logo is present
        <BrexFlagBlackBG height={32} width={32} />
      ) : (
        <ResponsiveIcon
          icon="RebirthLogo"
          responsive={{
            LRG: { height: 25 },
            MED: { height: 25 },
            SML: { height: 25 },
          }}
        />
      )}
    </Link>
  );

  const LogoTabletAndMobile = () => (
    <>
      {partnerLogo ? (
        <MobileLogoAndPartnerLogoContainer $shouldStack={shouldStackLogos}>
          <NavBarLogoBorder $shouldStack={shouldStackLogos}>
            <BrexLogoTabletMobile />
          </NavBarLogoBorder>
          <PartnerLogo src={partnerLogo} onLoad={handlePartnerLogoLoad} />
        </MobileLogoAndPartnerLogoContainer>
      ) : (
        <BrexLogoTabletMobile />
      )}
    </>
  );

  const btnBrexLogoDesktop = (
    <BrexLogoButtonContainerDesktop>
      <LogoDesktop />
    </BrexLogoButtonContainerDesktop>
  );

  const btnBrexLogoTabletAndMobile = (
    <BrexLogoButtonContainerTabletAndMobile>
      <LogoTabletAndMobile />
    </BrexLogoButtonContainerTabletAndMobile>
  );

  return (
    <>
      {btnBrexLogoDesktop}
      {btnBrexLogoTabletAndMobile}
    </>
  );
};

const SignInText = styled(Text)(({ theme }) => ({
  ...theme.typography.BodySmallM,
}));

export const SignInNavCTA: React.FC<{
  signInText: string;
  signInButtonText: string;
  signInUrl: string;
  ctaType?: "link" | "button";
}> = ({ signInText, signInButtonText, signInUrl, ctaType = "button" }) => {
  const { colors } = useTheme();

  return (
    <Container
      direction={"row"}
      display="flex"
      alignItems="center"
      justifyContent="center"
      padding={{
        LRG: [0, 0, 0, "xsml"],
        MED: [0, 0, 0, "xsml"],
        SML: [0, 0, 0, "xsml"],
      }}
    >
      <Text
        as="h3"
        color={colors.content.primary}
        margin={{
          LRG: [0, 16, 0, 0],
          MED: [0, 8, 0, 0],
          SML: [0, 8, 0, 0],
        }}
        components={{
          LRG: "Body2",
          MED: "Body2",
          SML: "Body2",
        }}
      >
        {signInText}
      </Text>
      {ctaType === "button" && (
        <SecondaryButton type="link" href={signInUrl}>
          {signInButtonText}
        </SecondaryButton>
      )}
      {ctaType === "link" && (
        <SignInLink
          href={signInUrl}
          label={signInButtonText}
          aria-label={signInButtonText}
          to={signInUrl}
          modifier="unstyled"
          component="a"
        >
          <SignInText as="span">{signInButtonText}</SignInText>
        </SignInLink>
      )}
    </Container>
  );
};

export const LeadFormNav: React.FC<{
  userPrefillData?: UserPrefillData;
  disableSignIn?: boolean;
  navigationConfig?: SignupNavigationConfig;
}> = ({ userPrefillData, disableSignIn = false, navigationConfig }) => {
  // Only enable hide-on-scroll when navigationConfig exists
  const hideOnScrollEnabled = !!navigationConfig;
  const isVisible = useHideNavbarOnScroll(hideOnScrollEnabled);

  const { userPrefillData: contextUserPrefillData } = useSSUPrefillContext();

  const prefillData = userPrefillData ?? contextUserPrefillData;

  // Use partnerLogoUrl from navigationConfig if provided, otherwise fall back to userPrefillData
  const partnerLogo =
    prefillData?.partnerLogoUrl &&
    !prefillData.partnerLogoUrl.includes("brex-internal")
      ? prefillData.partnerLogoUrl
      : undefined;

  const shouldDisableSignIn = useMemo(() => {
    // respect override from prop
    if (disableSignIn === true) return true;
    if (navigationConfig?.disableSignIn === true) return true;
    if (navigationConfig?.signInButtonLocation === "bottom") return true;
    return false;
  }, [disableSignIn, navigationConfig]);

  return (
    <LeadFormNavContainer $isVisible={isVisible}>
      <BrexLogoNavButton partnerLogo={partnerLogo} />
      {!shouldDisableSignIn && (
        <SignInNavCTA
          signInText={navigationConfig?.signInText || "Have an account?"}
          signInButtonText={navigationConfig?.signInButtonText || "Sign in"}
          signInUrl={
            navigationConfig?.signInUrl ||
            process.env.DASHBOARD_ORIGIN_URL ||
            "https://dashboard.brex.com/" // Fallback incase undefined
          }
          ctaType={navigationConfig?.signInCTAType || "button"}
        />
      )}
    </LeadFormNavContainer>
  );
};
